import { Button, Checkbox, Modal, UI, useConfirm, VStack, HStack } from '@genial/components';
import { Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Modal as NBModal, Text } from 'native-base';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../routes/types';
import { useRateManagementActions } from '../../../../../store/rateManagement/actions';

import {
  confirmMasterAccountTexts,
  getDefaultValues,
  getValidationCreateSchema,
  MasterAccountForm,
} from './constants';

export const ConfirmMasterAccount = () => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'ConfirmMasterAccount'>>();
  const sentOrderId = params.orderId;

  const { goBack, canGoBack, navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { sendOrder } = useRateManagementActions();

  const [isOpen, setIsOpen] = React.useState(true);
  const [isChecked, setIsChecked] = React.useState(false);
  const [applicationState, setApplicationState] = React.useState(
    getDefaultValues(params.managerAccountNumber, params.managerAccountDigit)
  );

  const { confirm } = useConfirm();
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const {
    control,
    setValue,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: applicationState,
    resolver: zodResolver(getValidationCreateSchema()),
  });

  const handleConfirm = handleSubmit(() => handleSendOrder(applicationState));

  const handleSendOrder = (data: MasterAccountForm) => {
    console.log(data);
    confirm(
      {
        isMobile: !isDesktop,
        description: (
          <Text>
            Ao enviar a solicitação, você confirma que a conta
            <Text fontWeight={Typography['fontweight.semibold']}>
              {' '}
              {data.account}-{data.digit}{' '}
            </Text>
            receberá o crédito?
          </Text>
        ),
        confirmText: 'Continuar',
        confirmColor: 'brand.tertiary.principal',
      },
      () => {
        const accountNumber = parseInt(data.account, 10);
        sendOrder(sentOrderId, accountNumber, data.digit);
        navigate('RateManagement');
      }
    );
  };

  const handleAccountInputChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setApplicationState((state) => ({ ...state, account: e.nativeEvent.text }));
    },
    [setApplicationState]
  );

  const handleDigitInputChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setApplicationState((state) => ({ ...state, digit: e.nativeEvent.text }));
    },
    [setApplicationState]
  );

  const toggleCheckbox = useCallback(() => {
    setIsChecked((prev) => {
      const digit = !prev ? 'X' : '';
      setValue('digit', digit, { shouldValidate: true });
      setApplicationState((state) => ({ ...state, digit }));

      return !prev;
    });
  }, [setValue]);

  const onClose = () => {
    setIsOpen(false);
    navigate('RateManagement');
  };

  const handleGoBack = () => {
    if (canGoBack()) {
      goBack();
    }
    navigate('RateManagement');
  };

  const modalContentDesktopProps = {
    maxH: 'full',
    maxW: '548px',
  };

  const modalContentMobileProps = {
    w: 'full',
    marginBottom: 0,
    marginTop: 'auto',
  };

  const modalContentProps = isDesktop ? modalContentDesktopProps : modalContentMobileProps;

  return (
    <>
      <Modal testId="modal-confirm-master-account" isOpen={isOpen} onClose={onClose}>
        <NBModal.Content maxW={'full'} {...modalContentProps}>
          <VStack
            p={Spacing['inset.md']}
            space={Spacing['stack.xxxs']}
            marginTop={Spacing['stack.md']}>
            <HStack justifyContent="center" space={Spacing['stack.xxxs']}>
              <UI.Text
                fontSize={Typography['fontsize.lg']}
                fontWeight={Typography['fontweight.semibold']}>
                {confirmMasterAccountTexts.title}
              </UI.Text>
            </HStack>

            <HStack
              alignItems={'center'}
              space={Spacing['stack.nano']}
              backgroundColor={Color['feedback.gen.lightest']}
              padding={Spacing['inset.nano']}
              borderRadius={Spacing['inline.nano']}>
              <UI.Icon name="Info" size="xxs" color={Color['brand.tertiary.principal']} />
              <UI.Text fontSize={Typography['fontsize.xs']} color={'brand.tertiary.principal'}>
                {confirmMasterAccountTexts.subtitle}
              </UI.Text>
            </HStack>

            <HStack flexDirection={'row'} space={Spacing['stack.xxxs']} height={'62px'}>
              <VStack space={Spacing['stack.nano']} flex={1}>
                <UI.Text fontWeight={Typography['fontweight.semibold']}>
                  {confirmMasterAccountTexts.labelAccount}
                </UI.Text>
                <UI.Input
                  name="account"
                  control={control}
                  autoComplete="off"
                  variant="filled"
                  placeholder={confirmMasterAccountTexts.accountPlaceholder}
                  keyboardType="numeric"
                  fontSize={Typography['fontsize.md']}
                  hasShadow={false}
                  error={errors.account}
                  onChange={handleAccountInputChange}
                />
              </VStack>

              <VStack space={Spacing['stack.nano']} width={isDesktop ? '26%' : '20%'}>
                <UI.Text fontWeight={Typography['fontweight.semibold']}>
                  {confirmMasterAccountTexts.labelDigit}
                </UI.Text>
                <UI.Input
                  name="digit"
                  control={control}
                  autoComplete="off"
                  variant="filled"
                  isDisabled={isChecked}
                  placeholder={confirmMasterAccountTexts.digitPlaceholder}
                  keyboardType="numeric"
                  fontSize={Typography['fontsize.md']}
                  hasShadow={false}
                  error={errors.digit}
                  onChange={handleDigitInputChange}
                />
              </VStack>
            </HStack>

            <Checkbox
              testId="checkbox-no-digit"
              selectedValues={isChecked ? ['X'] : []}
              onChange={() => {
                toggleCheckbox();
              }}
              options={[{ label: 'Esta conta não possui dígito ', value: 'X' }]}
            />

            <HStack justifyContent="center" flex={1} w="100%" alignItems="center">
              <Button
                mr={Spacing['stack.xxxs']}
                backgroundColor="transparent"
                size="lg"
                onPress={handleGoBack}
                shadow={1}
                flex={isDesktop ? 'none' : 1}>
                <Text
                  fontWeight={Typography['fontweight.semibold']}
                  fontSize={Typography['fontsize.md']}
                  color={Color['brand.primary.darkest'] as ColorTypes.Option}>
                  {confirmMasterAccountTexts.buttonBack}
                </Text>
              </Button>

              <Button
                backgroundColor={Color['brand.tertiary.principal']}
                size="lg"
                isDisabled={!isValid}
                onPress={handleConfirm}
                flex={isDesktop ? 'none' : 1}>
                <Text
                  fontWeight={Typography['fontweight.semibold']}
                  fontSize={Typography['fontsize.md']}
                  color={'#ffff' as ColorTypes.Option}>
                  {confirmMasterAccountTexts.buttonContinue}
                </Text>
              </Button>
            </HStack>
          </VStack>
        </NBModal.Content>
      </Modal>
    </>
  );
};
